import { Grid2 as Grid } from "@mui/material";
import { Address, OrganizationAddressInfo } from "../../types";
import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import {
  useFetchOrganizationInfoQuery,
  useUpdateOrganizationAddressMutation,
} from "../../api";
import { selectedOrgIdAtom, toastOptionsAtom } from "../../atoms";
import { OrganizationModel } from "../../models";
import { EditAddressInfo, ViewAddressInfo } from "./fragments";

export const OrganizationAddress = () => {
  const [isPrimaryAddressEditMode, setIsPrimaryAddressEditMode] =
    useState(false);
  const [isRemittanceAddressEditMode, setIsRemittanceAddressEditMode] =
    useState(false);
  const [selectedOrgId] = useAtom(selectedOrgIdAtom);
  const [, setToastOptions] = useAtom(toastOptionsAtom);
  const [country, setCountry] = useState<string>("US");

  const [addressInfo, setAddressInfo] = useState<OrganizationAddressInfo>();
  const fetchOrganizationInfoQuery =
    useFetchOrganizationInfoQuery(selectedOrgId);
  const updateOrganizationAddressMutation =
    useUpdateOrganizationAddressMutation();

  useEffect(() => {
    if (fetchOrganizationInfoQuery.isSuccess) {
      const addressInfo = OrganizationModel.buildOrganizationAddress(
        fetchOrganizationInfoQuery.data,
      );
      setAddressInfo(addressInfo);
      setCountry(fetchOrganizationInfoQuery.data.country);
    }
  }, [
    fetchOrganizationInfoQuery.isSuccess,
    fetchOrganizationInfoQuery.isError,
    fetchOrganizationInfoQuery.data,
    fetchOrganizationInfoQuery.error,
    fetchOrganizationInfoQuery.isLoading,
  ]);

  useEffect(() => {
    if (updateOrganizationAddressMutation.isSuccess) {
      fetchOrganizationInfoQuery.refetch();

      if (isRemittanceAddressEditMode) setIsRemittanceAddressEditMode(false);
      if (isPrimaryAddressEditMode) setIsPrimaryAddressEditMode(false);

      setToastOptions({
        open: true,
        message: "Address updated",
        severity: "success",
      });
    }

    if (updateOrganizationAddressMutation.isError) {
      if (isRemittanceAddressEditMode) setIsRemittanceAddressEditMode(false);
      if (isPrimaryAddressEditMode) setIsPrimaryAddressEditMode(false);

      setToastOptions({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    }
  }, [
    updateOrganizationAddressMutation.isSuccess,
    updateOrganizationAddressMutation.isError,
    updateOrganizationAddressMutation.isLoading,
    updateOrganizationAddressMutation.data,
    updateOrganizationAddressMutation.error,
  ]);

  const handleEditPrimaryAddress = (data: Omit<Address, "country">) => {
    if (isPrimaryAddressEditMode) setIsPrimaryAddressEditMode(false);
    updateOrganizationAddressMutation.mutate({
      orgId: selectedOrgId,
      primaryAddress: data,
    });
  };

  const handleEditRemittanceAddress = (data: Omit<Address, "country">) => {
    if (isRemittanceAddressEditMode) setIsRemittanceAddressEditMode(false);
    updateOrganizationAddressMutation.mutate({
      orgId: selectedOrgId,
      remittanceAddress: data,
    });
  };

  const handleDiscardEditPrimaryAddress = () => {
    setIsPrimaryAddressEditMode(false);
  };

  const handleDiscardEditRemittanceAddress = () => {
    setIsRemittanceAddressEditMode(false);
  };

  return (
    <Grid
      container
      direction={"row"}
      spacing={3}
      gap={3}
      sx={{
        direction: "row",
        borderRadius: "10px",
        px: "4px",
      }}
    >
      {!isPrimaryAddressEditMode ? (
        <ViewAddressInfo
          data={addressInfo?.primaryAddress}
          country={country}
          title={"Primary Address"}
          isLoading={
            fetchOrganizationInfoQuery.isLoading ||
            fetchOrganizationInfoQuery.isFetching ||
            updateOrganizationAddressMutation.isLoading
          }
          handleEditMode={setIsPrimaryAddressEditMode}
        />
      ) : (
        addressInfo && (
          <EditAddressInfo
            data={addressInfo.primaryAddress}
            country={country}
            handleEdit={handleEditPrimaryAddress}
            handleDiscard={handleDiscardEditPrimaryAddress}
          />
        )
      )}
      {!isRemittanceAddressEditMode ? (
        <ViewAddressInfo
          data={addressInfo?.remittanceAddress}
          country={country}
          title={"Remittance Address"}
          isLoading={
            fetchOrganizationInfoQuery.isLoading ||
            fetchOrganizationInfoQuery.isFetching ||
            updateOrganizationAddressMutation.isLoading
          }
          handleEditMode={setIsRemittanceAddressEditMode}
        />
      ) : (
        addressInfo && (
          <EditAddressInfo
            data={addressInfo.remittanceAddress}
            country={country}
            handleEdit={handleEditRemittanceAddress}
            handleDiscard={handleDiscardEditRemittanceAddress}
          />
        )
      )}
    </Grid>
  );
};
